import { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Award, TrendingUp, Copy, MessageCircle, Facebook, Twitter, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ShareButtons = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const shareText = `Use my code ${code} at Canadian Wellness & Fitness for 10% off your order! ${window.location.origin}`;
  const encoded = encodeURIComponent(shareText);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const nativeShare = () => {
    navigator.share({ text: shareText }).catch(() => {});
  };

  const btnCls = "flex items-center justify-center gap-1.5 bg-[#121212] border border-[#27272A] hover:border-[#D4AF37] text-gray-300 hover:text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5 rounded-sm transition-colors";

  return (
    <div className="mt-4" data-testid="ambassador-share-buttons">
      <p className="text-gray-500 text-[10px] uppercase tracking-widest mb-2">Share your code</p>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={copy} className={btnCls} data-testid="share-copy-btn">
          <Copy size={13} className="text-[#D4AF37]" /> {copied ? 'Copied!' : 'Copy Message'}
        </button>
        <a href={`sms:?&body=${encoded}`} className={btnCls} data-testid="share-sms-btn">
          <MessageCircle size={13} className="text-green-500" /> Text a Friend
        </a>
        <a href={`https://wa.me/?text=${encoded}`} target="_blank" rel="noopener noreferrer" className={btnCls} data-testid="share-whatsapp-btn">
          <MessageCircle size={13} className="text-green-400" /> WhatsApp
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encoded}`} target="_blank" rel="noopener noreferrer" className={btnCls} data-testid="share-facebook-btn">
          <Facebook size={13} className="text-[#1877F2]" /> Facebook
        </a>
        <a href={`https://twitter.com/intent/tweet?text=${encoded}`} target="_blank" rel="noopener noreferrer" className={btnCls} data-testid="share-twitter-btn">
          <Twitter size={13} className="text-[#1DA1F2]" /> X / Twitter
        </a>
        {navigator.share ? (
          <button onClick={nativeShare} className={btnCls} data-testid="share-native-btn">
            <Share2 size={13} className="text-[#D4AF37]" /> More...
          </button>
        ) : (
          <a href={`mailto:?subject=${encodeURIComponent('10% off at Canadian Wellness & Fitness')}&body=${encoded}`} className={btnCls} data-testid="share-email-btn">
            <Share2 size={13} className="text-[#D4AF37]" /> Email
          </a>
        )}
      </div>
    </div>
  );
};

const PointsChecker = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await axios.get(`${API}/ambassador-status`, { params: { email: email.trim() } });
      setStatus(res.data);
    } catch (err) {
      setStatus(null);
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const pct = status ? Math.min(100, Math.round((status.points / status.goal) * 100)) : 0;

  return (
    <div className="mt-4" data-testid="points-checker">
      <form onSubmit={check} className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={e => { setEmail(e.target.value); setError(''); }}
          placeholder="your ambassador email"
          className="flex-1 min-w-0 bg-[#121212] border border-[#27272A] focus:border-[#D4AF37] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
          data-testid="points-checker-email-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="bg-[#D4AF37] hover:bg-[#b8962f] disabled:opacity-50 text-black text-xs font-bold uppercase tracking-wider px-5 rounded-sm transition-colors whitespace-nowrap flex items-center gap-1.5"
          data-testid="points-checker-submit-btn"
        >
          <TrendingUp size={14} /> {busy ? '...' : 'Check My Points'}
        </button>
      </form>
      {error && <p className="text-[#C41E3A] text-xs mt-2" data-testid="points-checker-error">{error}</p>}
      {status && (
        <div className="bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-sm p-5 mt-3 text-left" data-testid="points-checker-result">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[#D4AF37] text-lg font-bold tracking-[0.25em]" data-testid="points-checker-code">{status.code}</p>
            <p className="text-white text-sm font-bold" data-testid="points-checker-points">{status.points} / {status.goal} pts</p>
          </div>
          <div className="h-2.5 bg-[#1a1a1a] rounded-full overflow-hidden mb-3">
            <div
              className={`h-full rounded-full transition-all duration-700 ${status.goal_reached ? 'bg-green-500' : 'bg-gradient-to-r from-[#C41E3A] to-[#D4AF37]'}`}
              style={{ width: `${pct}%` }}
              data-testid="points-checker-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <p className="text-gray-400">{status.uses} referral{status.uses !== 1 ? 's' : ''} so far{status.event_active ? ' • 2x points event is ON!' : ''}</p>
            <p className={status.goal_reached ? 'text-green-400 font-bold' : 'text-gray-400'}>
              {status.goal_reached ? `You've unlocked ${status.current_discount}% OFF!` : `${status.goal - status.points} pts to 20% OFF`}
            </p>
          </div>
          <ShareButtons code={status.code} />
          {status.bonus_active && (
            <p className="text-[#D4AF37] text-xs font-semibold mt-3" data-testid="points-checker-bonus">
              Thank-you bonus active — your code is stacked to {status.current_discount}% off on your next order!
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const AmbassadorSignup = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [createdCode, setCreatedCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showChecker, setShowChecker] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ambassador-signup`, { email: email.trim(), code: code.trim() });
      setCreatedCode(res.data.code);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (createdCode) {
    return (
      <div className="max-w-md mx-auto bg-[#0A0A0A] border border-[#D4AF37] rounded-sm p-6 text-center" data-testid="ambassador-success">
        <p className="flex items-center justify-center gap-2 text-green-400 text-sm font-semibold mb-3">
          <CheckCircle size={16} /> You&apos;re officially a CWF Ambassador!
        </p>
        <div className="bg-[#121212] border border-dashed border-[#D4AF37] rounded-sm px-4 py-3 mb-3">
          <p className="text-gray-400 text-[10px] uppercase tracking-widest">Your referral code</p>
          <p className="text-[#D4AF37] text-2xl font-bold tracking-[0.3em]" data-testid="ambassador-created-code">{createdCode}</p>
        </div>
        <p className="text-gray-400 text-xs leading-relaxed">
          Share it with friends — they get 10% off, you earn points toward 20% off at 100 points.
          Check your inbox for the full details!
        </p>
        <ShareButtons code={createdCode} />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto" data-testid="ambassador-signup">
      <form onSubmit={submit}>
        <div className="space-y-2">
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
            data-testid="ambassador-email-input"
          />
          <div className="flex gap-2">
            <input
              type="text"
              required
              value={code}
              onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
              placeholder="PICK YOUR CODE (e.g. SARAH10)"
              maxLength={15}
              className="flex-1 min-w-0 bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors uppercase tracking-widest"
              data-testid="ambassador-code-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-5 rounded-sm transition-all duration-300 whitespace-nowrap flex items-center gap-1.5"
              data-testid="ambassador-submit-btn"
            >
              <Award size={14} /> {busy ? '...' : 'Create My Code'}
            </button>
          </div>
        </div>
        {error && <p className="text-[#C41E3A] text-xs mt-2" data-testid="ambassador-error">{error}</p>}
      </form>
      <p className="text-gray-500 text-[11px] mt-3 leading-relaxed">
        Friends get <span className="text-[#D4AF37]">10% off</span> with your code • every referral earns you points •
        100 points upgrades your code to <span className="text-[#C41E3A] font-semibold">20% off</span>
      </p>
      <button
        type="button"
        onClick={() => setShowChecker(!showChecker)}
        className="text-[#D4AF37] hover:text-white text-xs underline underline-offset-4 mt-4 transition-colors"
        data-testid="points-checker-toggle"
      >
        Already an ambassador? Check your points progress
      </button>
      {showChecker && <PointsChecker />}
    </div>
  );
};

export default AmbassadorSignup;
